<template>
	<section class="download-multi-select">
		<div
			:class="[
				'download-multi-select__header',
				headlineParagraph || headlineParagraphSlot === true
					? 'download-multi-select__header--with-paragraph'
					: null
			]"
		>
			<template v-if="headline">
				<h2 v-if="Number(headingLevel) == 2">{{ headline }}</h2>
				<h3 v-else>{{ headline }}</h3>

				<template v-if="headlineParagraphSlot === true">
					<slot name="headline-text" />
				</template>

				<p
					v-if="headlineParagraph && headlineParagraphSlot === false"
					v-html="headlineParagraph"
				/>
			</template>
			<slot name="button">
				<!-- Set the prop `useOrderLitClickEvent` to `true` if you want to listen for the click event and
					 prevent the default behavior of navigating to the href url -->
				<template v-if="returnBoolean(useOrderLitClickEvent)">
					<button
						v-if="items.length > 0 && buttonText"
						class="button button--black"
						@click.prevent="onOrderLitButtonClick"
					>
						{{ buttonText }}
						<span class="icon icon-right" aria-hidden="true" />
					</button>
				</template>

				<!-- The default 'order lit' button -->
				<template v-else>
					<a
						v-if="items.length > 0 && orderLitURL && buttonText"
						class="button button--black"
						:href="orderLitURL"
					>
						{{ buttonText }}
						<span class="icon icon-right" aria-hidden="true" />
					</a>
				</template>
			</slot>
		</div>
		<FreeContent
			v-if="contentBefore"
			:content="contentBefore"
			class="download-multi-select__content-before"
		/>
		<form
			v-if="items.length > 0"
			class="download-multi-select__form"
			aria-label="Choose materials to download"
			ref="form"
		>
			<div class="download-multi-select__item-container">
				<DownloadMultiSelectItem
					v-if="returnBoolean(selectAll)"
					:id="selectAllId"
					class="select-all-item"
					:displayTitle="'Select all'"
					:hideDownloadIcon="true"
					:isChecked="shouldCheckSelectAll"
					:alignForEyebrow="firstItemHasEyebrow"
					@update:selectedItems="toggleAll"
				/>
				<DownloadMultiSelectItem
					v-for="(item, i) in items"
					class="download-item"
					:key="`multi-select-item-${i}`"
					:id="`${multiSelectId}-item-${i}`"
					:displayTitle="item.displayTitle"
					:downloadTitle="item.downloadTitle"
					:description="item.description"
					:eyebrow="item.eyebrow"
					:link="item.relativePath"
					:forceAsAbsolutePath="returnBoolean(item.forceAsAbsolutePath)"
					:litId="item.literatureId"
					:fundId="item.fundId"
					:lockItem="returnBoolean(item.lockItem)"
					:anchorLink="returnBoolean(item.anchorLink)"
					:anchorLinkTargetBlank="returnBoolean(item.anchorLinkTargetBlank)"
					:anchorLinkTitle="item.anchorLinkTitle"
					:lockContent="returnBoolean(lockContent)"
					:lockMessage="lockMessage"
					:lightFontTheme="returnBoolean(lightFontTheme)"
					@update:selectedItems="updateSelectedItems"
					@LockedItemClicked="onLockedItemClicked"
					@DownloadButtonClicked="onDownloadButtonClicked"
				/>
			</div>

			<div
				v-if="showDownloadBar"
				class="download-multi-select__download-bar fg--white"
			>
				<p class="download-multi-select__selected-text">
					{{ selectedItems.length }}
					{{ selectedItems.length === 1 ? "document" : "documents" }} selected
					{{ afterSelectedText }}
				</p>

				<button
					type="button"
					class="download-multi-select__submit button button--inverted"
					@click="downloadSelected"
					:title="buttonTitle"
				>
					Download ({{ selectedItems.length }})
					<span class="icon icon-download" aria-hidden="true" />
				</button>
			</div>
			<div
				v-if="showDownloadedBar"
				class="downloaded-bar download-multi-select__download-bar fg--white"
				:class="{ fade: fadeOut }"
			>
				<p class="download-multi-select__selected-text">
					{{ totalDownloaded }}
					{{ totalDownloaded == 1 ? "document" : "documents" }}
					downloaded
					{{ afterSelectedText }}
					<span v-if="totalDownloaded > 0" class="icon icon-check" />
				</p>
			</div>
		</form>
		<ErrorState v-else :message="errorMessage" />
		<FreeContent
			v-if="contentAfter"
			:content="contentAfter"
			class="download-multi-select__content-after"
		/>
	</section>
</template>

<script>
import DownloadMultiSelectItem from "./base/DownloadMultiSelectItem.vue";
import DownloadErrorModal from "./base/DownloadErrorModal.vue";
import { FreeContent } from "../free-content";
import ErrorState from "../error-state/ErrorState.vue";
import fileDownloaderService from "./services/fileDownloader.service.js";
import { EventBus } from "@vg-faset/core";
import { UniqueId } from "../../../utilities";
import { adobeLaunch } from "vg-fas-adobe-launch";

/**
 * A multi-select download component.
 *
 * ## Props Data
 *
 * ### `items`
 *
 * ```javascript
 * [
 *	{
 *		anchorLink: false, // (Optional) For anchor links, set this item to true
 *		literatureId: "SP40", // (Optional) ID of the literature item.
 *		displayTitle: "Summary prospectus", // (Required) Document title which is displayed on screen
 *		downloadTitle: "VFINX Summary prospectus", // (Required) Title given to downloaded PDF
 *		relativePath: "/pub/Pdf/sp40.pdf", // (Required) Relative path to document (see forceAsAbsolutePath for absolute paths)
 *		forceAsAbsolutePath: false, // (Optional) If the asset download path needs to be absolute, set this to true
 *		titleLink: {
 *			location: "/whitepaper.pdf",
 *			text: "Order Whitepaper"
 *		}, // (Optional) Link next to document title
 *		description:
 *			"A simplified version of a statutory prospectus.", // (Optional) Description of the document
 *		extraDownload: {
 *			location: "/FINRA-letter.pdf",
 *			text: "FINRA Letter"
 *		}, // (Optional) Additional download button next to Download PDF button
 *		img: "/assets/img.jpg" // (Optional) Image of document cover
 *	}
 *]
 * ```
 *
 */
export default {
	name: "DownloadTool",
	components: { DownloadMultiSelectItem, FreeContent, ErrorState },
	props: {
		/**
		 * The headline text appearing within an h2 or h3 depending on the `headingLevel` prop value.
		 */
		headline: {
			type: String
		},

		/**
		 * Paragraph that's located below the heading.
		 */
		headlineParagraph: {
			type: String
		},

		/**
		 * Turn on and off the paragraph slot content. Slot name: 'headline-text'.
		 */
		headlineParagraphSlot: {
			type: Boolean,
			default: false
		},

		/**
		 * Text within the download button.
		 */
		buttonText: {
			type: String
		},

		/**
		 * Allow for the locking of any content flagged to be locked without being logged in.
		 */
		lockContent: {
			type: [Boolean, String]
		},

		/**
		 * A message describing that the item is locked.
		 */
		lockMessage: {
			type: String
		},

		/**
		 * Turn the 'Select all' option on or off.
		 */
		selectAll: {
			type: [Boolean, String],
			default: true
		},

		/**
		 * An array of objects making up the downloadable items. Refer to the documentation above for object properties.
		 */
		items: {
			type: Array,
			default: () => []
		},

		/**
		 * Can be 2 or 3 representing which heading tag to use (H2 or H3).
		 */
		headingLevel: {
			type: [Number, String],
			validator: function (value) {
				return !isNaN(value);
			}
		},

		/**
		 * Add a URL to the button so it behaves like an anchor link rather than using a click event.
		 */
		orderLitURL: {
			type: String
		},

		/**
		 * A message to display when there is an error downloading an item.
		 */
		errorMessage: {
			type: String
		},

		/**
		 * Text content continue the sentence that appears when 1 or more items are selected. Usage: `${numberDownloaded} documents selected ${afterSelectedText}`.
		 */
		afterSelectedText: {
			type: String,
			default: ""
		},

		/**
		 * Use the emitted click event only on the top-right button and prevent any default actions.
		 */
		useOrderLitClickEvent: {
			type: [Boolean, String],
			default: false
		},

		/**
		 * Text content or HTML markup to place before the selectable list of downloadable items.
		 */
		contentBefore: {
			type: String,
			default: ""
		},

		/**
		 * Text content or HTML markup to place after the selectable list of downloadable items.
		 */
		contentAfter: {
			type: String,
			default: ""
		},

		/**
		 * Set this prop value to `true` (boolean) if your items have descriptions but you want the document titles to remain at the normal font weight (not bold). This overrides the styles and sets the document title font weight to 'normal' and the description's 'p3' style font weight to 300 (light).
		 */
		lightFontTheme: {
			type: [Boolean, String],
			default: false
		}
	},
	computed: {
		shouldCheckSelectAll() {
			return this.items.length - this.lockCount === this.selectedItems.length;
		},
		showDownloadBar() {
			return this.selectedItems.length > 0;
		},
		showDownloadedBar() {
			return this.selectedItems.length === 0 && this.recentlyDownloaded;
		},
		totalDownloaded() {
			return (
				this.lastSelectedCount - Object.keys(this.failedDownloadsArr).length
			);
		},
		firstItemHasEyebrow() {
			return "eyebrow" in this.items[0];
		},
		buttonTitle() {
			const fileExtensionArray = [];
			let fileNames = "";

			this.selectedItems.forEach((el) => {
				fileExtensionArray.push(el.link.split(".").pop().toLowerCase());
			});

			const allEqual = (arr) => arr.every((val) => val === arr[0]);
			const result = allEqual(fileExtensionArray);

			if (fileExtensionArray.length > 1) {
				if (result === false) {
					fileNames = `Download selected files`;
				} else {
					fileNames = `Download selected ${fileExtensionArray[0].toUpperCase()} files`;
				}
			} else {
				fileNames = `Download selected ${fileExtensionArray[0].toUpperCase()} file`;
			}

			return fileNames;
		}
	},
	methods: {
		onOrderLitButtonClick() {
			/**
			 * Emits when the top-right button is clicked.
			 *
			 * @action
			 */
			this.$emit("order-lit-button-clicked", true);
		},
		onDownloadButtonClicked(id) {
			/**
			 * Emits when the download icon is clicked and passes the item ID.
			 *
			 * @action
			 */
			this.$emit("DownloadButtonClicked", id);
		},
		updateSelectedItems(checkboxStatusEvent) {
			/**
			 * Emits when a selected item is clicked and passes the item ID.
			 *
			 * @action
			 */
			this.$emit("SelectItemClicked", checkboxStatusEvent.id);

			this.recentlyDownloaded = false;
			this.fadeOut = false;

			this.lockCount = this.$refs.form.querySelectorAll(".icon-lock").length;
			this.ctaCount = this.$refs.form.querySelectorAll(".anchor").length;

			if (
				!this.selectedItems.some((item) => item.id === checkboxStatusEvent.id)
			) {
				this.selectedItems.push(checkboxStatusEvent);
				if (this.shouldCheckSelectAll) {
					EventBus.$emit("download-tool:showDisclaimer");
				}
			} else {
				this.selectedItems = this.selectedItems.filter(
					(item) => item.id !== checkboxStatusEvent.id
				);
				EventBus.$emit("download-tool:hideDisclaimer");
			}
		},
		toggleAll() {
			const checkboxes = this.$refs.form.querySelectorAll(
				'input[type="checkbox"]'
			);

			if (
				this.items.length - (this.lockCount + this.ctaCount) !==
				this.selectedItems.length
			) {
				for (const checkbox of checkboxes) {
					if (checkbox.checked === false) {
						checkbox.click();
					}
				}
			} else {
				for (const checkbox of checkboxes) {
					if (checkbox.checked === true) {
						checkbox.click();
					}
				}
			}
			this.displayNonRegisterMessage();
		},
		displayNonRegisterMessage() {
			const disabledButtons = this.$refs.form.querySelectorAll(".grey");
			if (disabledButtons.length > 0) {
				for (const button of disabledButtons) {
					button.click();
				}
			}
		},
		downloadSelected() {
			this.failedDownloadsArr = [];
			for (const item of this.selectedItems) {
				const fileName = item.link.substring(
					item.link.lastIndexOf("/") + 1,
					item.link.lastIndexOf(".")
				);
				const fileExtension = item.link.split(".").pop();

				const litIdTrack = item.litId ? `lit:${item.litId}` : null;

				adobeLaunch.trackContentDownloaded(
					`${fileExtension.toLowerCase()}:${fileName}`,
					litIdTrack,
					item.fundId
				);

				fileDownloaderService
					.downloadFileFromLink(
						item.link,
						item.forceAsAbsolutePath,
						`${item.downloadTitle}.${fileExtension}`
					)
					.catch(() => {
						this.failedDownloadsArr.push(item.displayTitle);
						this.showDownloadErrorModal();
					});
			}
			this.downloadCompleted();
		},
		downloadCompleted() {
			this.lastSelectedCount = this.selectedItems.length;

			const selectAll = this.$refs.form.querySelector(
				".select-all-item input[type='checkbox']"
			);
			const checkboxes = this.$refs.form.querySelectorAll(
				".download-item input[type='checkbox']"
			);

			if (selectAll && selectAll.checked) {
				selectAll.click();
			} else {
				for (const checkbox of checkboxes) {
					if (checkbox.checked === true) {
						checkbox.click();
					}
				}
			}

			this.selectedItems = [];
			this.recentlyDownloaded = true;
			this.fadeOut = true;

			// 5500 is currently what's set in SCSS below (5s delay, .5s fade-out).
			// Make sure the values are in sync.
			setTimeout(() => (this.recentlyDownloaded = false), 5500);
		},
		showDownloadErrorModal() {
			let modalData = {
				failedDownloadsArr: this.failedDownloadsArr
			};
			EventBus.$emit("modal:set", DownloadErrorModal);
			EventBus.$emit("modal:show");
			EventBus.$emit("modal:set-data", modalData);
		},
		onLockedItemClicked() {
			/**
			 * Emits when a locked item is clicked.
			 *
			 * @action
			 */
			this.$emit("LockedItemClicked");
		},
		onOrderClick() {
			/**
			 * @ignore
			 */
			this.$emit("OrderLitClicked");
		},
		returnBoolean(prop) {
			return prop === true ? true : prop === "true" ? true : false;
		}
	},

	data() {
		return {
			lockCount: 0,
			ctaCount: 0,
			selectedItems: [],
			lastSelectedCount: 0,
			failedDownloadsArr: [],
			recentlyDownloaded: false,
			selectAllId: new UniqueId("select-all-").generate(),
			multiSelectId: new UniqueId("multi-select-").generate(),
			fadeOut: false
		};
	}
};
</script>

<style lang="scss">
.download-multi-select {
	padding-bottom: 51px;
	padding-top: 51px;

	@include from-md() {
		padding-bottom: 62px;
		padding-top: 88px;
	}

	&__header {
		align-items: center;
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;

		h3,
		h2 {
			margin: 0;
			@include at-md {
				max-width: 300px;
			}
		}

		a.button {
			display: inline-block;

			@include to-md {
				flex-basis: 1;
				margin-top: 25px;
				width: 100%;
			}

			@include from-md {
				margin-left: 25px;
			}
		}

		&--with-paragraph {
			h2,
			h3 {
				min-width: 100%;
				width: 100%;
			}
		}
	}

	&__select-all {
		cursor: pointer;
		display: inline-block;
		font-size: 14px;
		font-weight: $font-weight--bold;

		@include from-md {
			margin-left: auto;
		}
	}

	&__item-container {
		border-top: 2px solid $color--lighter-gray;
		display: flex;
		flex-wrap: wrap;
		margin-top: 24px;

		.input-check {
			border-bottom: 2px solid $color--lighter-gray;
			flex-basis: 100%;

			@include from-md {
				flex-basis: 50%;

				// no bottom border on last element if it's an odd number
				&:nth-last-child(2):nth-child(odd) {
					border-bottom: none;
				}
			}

			// no bottom border on last item
			&:last-child {
				border-bottom: none;
			}
		}
	}

	&__download-bar {
		align-items: center;
		background-color: $color--red;
		display: flex;
		justify-content: space-between;
		padding: 20px 25px;
	}

	&__selected-text {
		font-weight: $font-weight--bold;

		@include to-md {
			display: none;
		}
	}

	&__submit {
		@include to-md {
			width: 100%;
		}
	}

	&__content-before,
	&__content-after {
		margin-top: 24px;
	}
}

.download-multi-select__header {
	row-gap: 18px;
	column-gap: 8px;
}

@include from-md {
	h2 + .download-multi-select .download-multi-select__header {
		margin-top: -6px;
	}
}

.downloaded-bar {
	height: 90px;

	&.fade {
		animation: fade-out 0.5s 1;
		animation-fill-mode: forwards;
		animation-delay: 5s;
	}

	.download-multi-select__selected-text {
		display: block;
	}
}

@keyframes fade-out {
	from {
		opacity: 1;
	}
	to {
		opacity: 0;
	}
}
</style>
